import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiArrowLeft,
  FiStar,
  FiMapPin,
  FiClock,
  FiDollarSign,
} from 'react-icons/fi';
import { Card, LoaderComponent, EmptyState } from '../../components/ui';
import driverService, { DeliveryHistory } from '../../services/driver.service';

const DriverHistoryPage: React.FC = () => {
  const navigate = useNavigate();
  const [deliveries, setDeliveries] = useState<DeliveryHistory[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 20;

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const res = await driverService.getHistory(page, limit);
        setDeliveries(res.data.data);
        setTotal(res.data.pagination.total);
      } catch (error) {
        console.error('Error loading history:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [page]);

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-CV', { style: 'currency', currency: 'CVE' }).format(value);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('pt-CV', {
      timeZone: 'Atlantic/Cape_Verde',
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="max-w-2xl mx-auto p-4 space-y-4">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate('/driver')} className="p-2 hover:bg-gray-100 rounded-lg">
          <FiArrowLeft />
        </button>
        <h1 className="text-xl font-bold">Historico de entregas</h1>
      </div>

      {loading ? (
        <LoaderComponent />
      ) : deliveries.length === 0 ? (
        <EmptyState
          icon={<FiClock className="text-4xl" />}
          title="Sem historico"
          description="As tuas entregas concluidas vao aparecer aqui."
        />
      ) : (
        <>
          <div className="space-y-3">
            {deliveries.map((d) => (
              <Card key={d.id} className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <p className="font-semibold text-gray-900">{d.restaurantName}</p>
                    <div className="flex items-center gap-1 text-xs text-gray-500 mt-0.5">
                      <FiMapPin className="text-xs" />
                      <span className="truncate max-w-[200px]">{d.deliveryAddress}</span>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="font-bold text-green-600">{formatCurrency(d.deliveryFee || 0)}</p>
                    <span
                      className={`text-xs px-2 py-0.5 rounded-full ${
                        d.status === 'ENTREGUE'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-red-100 text-red-700'
                      }`}
                    >
                      {d.status === 'ENTREGUE' ? 'Entregue' : 'Cancelado'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-4 text-xs text-gray-400 pt-2 border-t border-gray-100">
                  <span className="flex items-center gap-1">
                    <FiClock /> {formatDate(d.deliveredAt || d.assignedAt)}
                  </span>
                  {d.distanceKm && <span>{d.distanceKm} km</span>}
                  {d.deliveryRating && (
                    <span className="flex items-center gap-1 text-yellow-500">
                      <FiStar /> {d.deliveryRating}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <FiDollarSign /> Pedido: {formatCurrency(d.orderTotal)}
                  </span>
                </div>
              </Card>
            ))}
          </div>

          {total > limit && (
            <div className="flex justify-center gap-2 pt-4">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-2 border rounded-lg disabled:opacity-30"
              >
                Anterior
              </button>
              <span className="px-4 py-2 text-sm text-gray-500">
                {page} / {Math.ceil(total / limit)}
              </span>
              <button
                onClick={() => setPage((p) => p + 1)}
                disabled={page * limit >= total}
                className="px-4 py-2 border rounded-lg disabled:opacity-30"
              >
                Proximo
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DriverHistoryPage;
